/**
 * One Products finance hub row: product toggle, partner/method dropdowns, Partner Sync.
 */
import React, { useEffect, useRef } from 'react';
import type { MenuProps } from 'antd';
import { Button, Spin } from 'antd';
import { CaretDown, Plugs } from '@phosphor-icons/react';
import { OBDDropdown } from './form-elements';
import { useProductsFinanceHubContext } from './ProductsFinanceHubContext';
import { isGoodLeap, partnerKeyForSyncModal } from './productsFinanceHubHelpers';
import type { ProductFinanceHubRowProps } from './ProductsFinanceHubContext.types';

export const ProductFinanceHubRow: React.FC<ProductFinanceHubRowProps> = ({
    productName,
    variant = 'default',
}) => {
    const {
        theme,
        rows,
        getRow,
        handleProductChange,
        setPartnerForProduct,
        setMethodForProduct,
        partnerOptionsForProduct,
        isProductDisabled,
        isMethodDropdownDisabled,
        isOpportunityInfoReadOnly,
        getCapabilityValue,
        hasUnsavedChanges,
        hasProductUnsavedChanges,
        noticeAPI,
        setLeadSyncFormOpen,
        setPartnerModal,
        menuPopupClass,
        handleSaveProduct,
        saveLoadingProduct,
        svaE4ExcelBusy,
        getProductValidationError,
        opportunityId,
    } = useProductsFinanceHubContext();

    const r = getRow(productName);
    const autoActivatedRef = useRef(false);

    useEffect(() => {
        if (variant !== 'card' || autoActivatedRef.current || !r) return;
        if (r.activeFlag || isOpportunityInfoReadOnly || isProductDisabled(productName)) return;
        autoActivatedRef.current = true;
        handleProductChange(productName, true);
    }, [
        variant,
        r,
        productName,
        isOpportunityInfoReadOnly,
        isProductDisabled,
        handleProductChange,
    ]);

    if (!r) return null;

    const active = !!r.activeFlag;
    const isCardEmbed = variant === 'card';
    const pNameRaw = String(r.finance_Partner_Display_Name || '').trim();
    const partnerOptionsForThisProduct = partnerOptionsForProduct(productName);
    const partnerOptionsReady = partnerOptionsForThisProduct.length > 1;
    const partnerIsInOptions =
        !!pNameRaw &&
        pNameRaw.toLowerCase() !== 'select finance partner' &&
        partnerOptionsForThisProduct.some((o) => o.value === pNameRaw);
    const hasPartner =
        (active || isCardEmbed) &&
        !!pNameRaw &&
        pNameRaw.toLowerCase() !== 'select finance partner' &&
        (partnerOptionsReady ? partnerIsInOptions : true);

    const mNameRaw = String(r.finance_Method_Name || '').trim();
    const methodOptionsReady = (r.methodOptions || []).length > 0;
    const methodIsInOptions =
        !!mNameRaw &&
        mNameRaw.toLowerCase() !== 'select method' &&
        (r.methodOptions || []).includes(mNameRaw);
    const hasMethod = active && methodIsInOptions;
    const isGoodLeapRow = isGoodLeap(pNameRaw);
    const isCashRow = pNameRaw.toLowerCase().includes('cash');
    const goodLeapFlowBlocked =
        isGoodLeapRow && getCapabilityValue('GoodLeap_AccountSearch') !== 'View';

    const partnerItems: MenuProps['items'] = partnerOptionsForThisProduct.map((opt) => ({
        key: opt.value,
        label: opt.label,
        disabled: opt.value === '',
    }));
    const methodOpts = r.methodOptions || [];
    const methodItems: MenuProps['items'] =
        methodOpts.length > 0
            ? methodOpts.map((m: string) => ({ key: m, label: m }))
            : [{ key: '__none', label: 'No methods loaded', disabled: true }];

    const partnerInteractive = !isOpportunityInfoReadOnly && (active || isCardEmbed);
    const methodDropdownDisabled =
        isOpportunityInfoReadOnly ||
        (!active && !isCardEmbed) ||
        !hasPartner ||
        r.methodsLoading ||
        isMethodDropdownDisabled(r, rows);

    const handleProductToggle = () => handleProductChange(productName, !active);

    const handlePartnerSelect = ({ key }: { key: string }) => {
        if (!active && isCardEmbed) {
            setPartnerForProduct(productName, String(key), { activate: true });
            return;
        }
        setPartnerForProduct(productName, String(key));
    };

    const handleMethodSelect = ({ key }: { key: string }) => {
        if (key !== '__none') setMethodForProduct(productName, String(key));
    };

    const handlePartnerSync = () => {
        if (isOpportunityInfoReadOnly || goodLeapFlowBlocked) return;
        if (hasUnsavedChanges) {
            noticeAPI.warning({
                message: 'Unsaved Changes Found',
                description:
                    'You have unsaved changes in your product settings. Please save your progress before initiating a partner sync.',
                placement: 'topRight',
                className: `obd-notification obd-theme-${theme}`,
            });
            return;
        }
        if (isGoodLeapRow) {
            const syncKey = partnerKeyForSyncModal(r.finance_Partner_Display_Name ?? '');
            setLeadSyncFormOpen(true, 'sync_existing', syncKey, {
                hideCreateLead: true,
            });
            return;
        }
        setPartnerModal(partnerKeyForSyncModal(r.finance_Partner_Display_Name ?? ''));
    };

    const rowClassName =
        variant === 'card' ? 'obd-pf-row obd-pf-row--card' : 'obd-pf-row';
    const cardValidationError = isCardEmbed ? getProductValidationError(productName) : null;
    const cardSaveLoading = isCardEmbed && saveLoadingProduct === productName;
    const showCardSave = isCardEmbed && hasProductUnsavedChanges(productName);

    return (
        <div className={rowClassName}>
            {isCardEmbed ? (
                <p className="obd-sd-card__config-label">Configuration</p>
            ) : null}
            {!isCardEmbed ? (
            <button
                type="button"
                className={`obd-pf-prod-btn ${active ? 'obd-pf-prod-btn--on' : 'obd-pf-prod-btn--off'} ${isProductDisabled(productName) ? 'obd-pf-prod-btn--disabled' : ''}`}
                onClick={handleProductToggle}
                disabled={isOpportunityInfoReadOnly || isProductDisabled(productName)}
            >
                {productName}
            </button>
            ) : null}
            <OBDDropdown
                menu={{ items: partnerItems, onClick: handlePartnerSelect }}
                trigger={['click']}
                disabled={!partnerInteractive}
                overlayClassName={menuPopupClass}
            >
                <button
                    type="button"
                    className={`obd-pf-menu-trigger ${!partnerInteractive ? 'obd-pf-menu-trigger--disabled' : ''}`}
                    disabled={!partnerInteractive}
                >
                    <span className="obd-pf-menu-trigger__text">
                        {active || isCardEmbed
                            ? pNameRaw
                                ? (partnerOptionsReady ? partnerIsInOptions : true)
                                    ? pNameRaw
                                    : ''
                                : 'Select Finance'
                            : 'Select Finance'}
                    </span>
                    <CaretDown size={14} weight="bold" aria-hidden />
                </button>
            </OBDDropdown>
            <div className="obd-pf-row__method">
                <OBDDropdown
                    menu={{ items: methodItems, onClick: handleMethodSelect }}
                    trigger={['click']}
                    disabled={methodDropdownDisabled}
                    overlayClassName={menuPopupClass}
                >
                    <button
                        type="button"
                        className={`obd-pf-menu-trigger obd-pf-menu-trigger--method ${((!active && !isCardEmbed) || !hasPartner || isMethodDropdownDisabled(r, rows)) ? 'obd-pf-menu-trigger--disabled' : ''} ${!hasMethod ? 'obd-pf-menu-trigger--placeholder' : ''}`}
                        disabled={methodDropdownDisabled}
                    >
                        <span className="obd-pf-menu-trigger__text">
                            {r.methodsLoading
                                ? mNameRaw || <Spin size="small" />
                                : !hasPartner
                                  ? 'Select Method'
                                  : mNameRaw
                                    ? (methodOptionsReady && !isMethodDropdownDisabled(r, rows)
                                          ? methodIsInOptions
                                          : true)
                                        ? mNameRaw
                                        : ''
                                    : 'Select Method'}
                        </span>
                        <CaretDown size={14} weight="bold" aria-hidden />
                    </button>
                </OBDDropdown>

                {active && hasPartner && !isCashRow && !isCardEmbed ? (
                    <Button
                        type="primary"
                        size="small"
                        className="obd-products-hub__partner-sync-btn obd-pf-row__partner-sync"
                        icon={<Plugs size={16} weight="duotone" aria-hidden />}
                        disabled={
                            isOpportunityInfoReadOnly ||
                            !r.finance_Partner_Display_Name?.trim() ||
                            goodLeapFlowBlocked
                        }
                        onClick={handlePartnerSync}
                    >
                        Partner Sync
                    </Button>
                ) : null}
            </div>
            {showCardSave ? (
                <div className="obd-pf-row__card-save">
                    {cardValidationError ? (
                        <p className="obd-pf-row__card-save-hint" role="alert">
                            {cardValidationError}
                        </p>
                    ) : null}
                    <Button
                        type="primary"
                        size="small"
                        className="obd-products-hub__save-all obd-pf-row__card-save-btn"
                        loading={cardSaveLoading}
                        onClick={() => void handleSaveProduct(productName)}
                        disabled={isOpportunityInfoReadOnly || !opportunityId || svaE4ExcelBusy}
                    >
                        {`Save ${productName} settings`}
                    </Button>
                </div>
            ) : null}
        </div>
    );
};
